import React, { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { usePieceOverlay } from '../../hooks/usePieceOverlay';
import { useTouchDrag } from '../../hooks/useTouchDrag';

// Import refactored components
import {
  ReferenceImage,
  DifficultyConfirmDialog,
  NewPuzzleConfirmDialog,
  PUZZLE_LOSS_ACTIONS,
  MobileHeader,
  DesktopHeader,
} from './controls';

export function GameControls({ 
  time, 
  moves, 
  difficulty,
  onDifficultyChange,
  isPaused, 
  isRunning,
  onPause, 
  onPrevious, 
  onRandom,
  hasNewImages = true,
  canGoBack = false,
  onReset,
  onSettings,
  onLeaderboard,
  onDailyChallenge,
  onChallengeFriend,
  onHistory,
  onHelp,
  hasPlayedDailyToday,
  imageUrl,
  notificationCount = 0,
  notifications = [],
  onMarkNotificationsRead,
  onViewChallengeNotification,
  isLandscape = false,
  isMobile = true,
  hideReferenceImage = false,
  onHint,
  hasUnplacedPieces = false,
  isComplete = false,
  hintPenalty = 0,
}) {
  // State
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [showNoNewImagesMessage, setShowNoNewImagesMessage] = useState(false);
  const [isDragHovering, setIsDragHovering] = useState(false);
  const [pendingDifficulty, setPendingDifficulty] = useState(null);
  const [showDifficultyConfirm, setShowDifficultyConfirm] = useState(false);
  const [showPuzzleLossConfirm, setShowPuzzleLossConfirm] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { type: PUZZLE_LOSS_ACTIONS, callback: fn }
  
  // Refs
  const refImageRef = useRef(null);
  
  // Piece overlay context for comparing pieces against reference image
  const pieceOverlay = usePieceOverlay();
  
  // Touch drag context for mobile piece dragging detection
  const touchDrag = useTouchDrag();
  
  // Check if game is in progress (has moves or timer running)
  const isGameInProgress = moves > 0 || isRunning;
  
  // Handle difficulty change with confirmation if game in progress
  const handleDifficultySelect = useCallback((newDifficulty) => {
    if (newDifficulty === difficulty) return;
    
    if (isGameInProgress) {
      setPendingDifficulty(newDifficulty);
      setShowDifficultyConfirm(true);
    } else {
      onDifficultyChange(newDifficulty);
    }
  }, [difficulty, isGameInProgress, onDifficultyChange]);
  
  const confirmDifficultyChange = useCallback(() => {
    if (pendingDifficulty) {
      onDifficultyChange(pendingDifficulty);
    }
    setShowDifficultyConfirm(false);
    setPendingDifficulty(null);
  }, [pendingDifficulty, onDifficultyChange]);
  
  const cancelDifficultyChange = useCallback(() => {
    setShowDifficultyConfirm(false);
    setPendingDifficulty(null);
  }, []);
  
  // Check if drag position is over the reference image
  const checkDragOverRef = useCallback((clientX, clientY) => {
    if (!refImageRef.current) return false;
    const rect = refImageRef.current.getBoundingClientRect();
    return (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    );
  }, []);
  
  // Global touch move listener to detect drag over reference image (for mobile)
  useEffect(() => {
    if (!pieceOverlay || pieceOverlay.isOverlayActive) return;
    if (!touchDrag?.isDragging || !touchDrag?.draggingPiece) return;
    
    const handleGlobalTouchMove = (e) => {
      if (!touchDrag.isDragging || !touchDrag.draggingPiece) return;
      
      const touch = e.touches[0];
      if (!touch) return;
      
      const isOverRef = checkDragOverRef(touch.clientX, touch.clientY);
      setIsDragHovering(isOverRef);
      
      if (isOverRef && !pieceOverlay.isOverlayActive) {
        pieceOverlay.activateOverlay(touchDrag.draggingPiece.piece, touch.clientX, touch.clientY);
      }
    };
    
    window.addEventListener('touchmove', handleGlobalTouchMove, { passive: true });
    return () => window.removeEventListener('touchmove', handleGlobalTouchMove);
  }, [touchDrag?.isDragging, touchDrag?.draggingPiece, pieceOverlay, checkDragOverRef]);
  
  // Reset drag hover state when drag ends
  useEffect(() => {
    if (!touchDrag?.isDragging) {
      setIsDragHovering(false);
    }
  }, [touchDrag?.isDragging]);
  
  // Handler for New Image button when no new images available
  const handleNewImageClick = useCallback(() => {
    if (!hasNewImages) {
      setShowNoNewImagesMessage(true);
      setTimeout(() => setShowNoNewImagesMessage(false), 5000);
      return;
    }
    
    // Show confirmation if game is in progress
    if (isGameInProgress) {
      setPendingAction({ type: PUZZLE_LOSS_ACTIONS.NEW_PUZZLE, callback: onRandom });
      setShowPuzzleLossConfirm(true);
      return;
    }
    
    onRandom?.();
  }, [hasNewImages, isGameInProgress, onRandom]);
  
  // Handler for Previous Image button
  const handlePreviousClick = useCallback(() => {
    if (!canGoBack) return;
    
    // Show confirmation if game is in progress
    if (isGameInProgress) {
      setPendingAction({ type: PUZZLE_LOSS_ACTIONS.PREVIOUS_IMAGE, callback: onPrevious });
      setShowPuzzleLossConfirm(true);
      return;
    }
    
    onPrevious?.();
  }, [canGoBack, isGameInProgress, onPrevious]);
  
  // Handler for Daily Challenge button
  const handleDailyChallengeClick = useCallback(() => {
    // Show confirmation if game is in progress
    if (isGameInProgress) {
      setPendingAction({ type: PUZZLE_LOSS_ACTIONS.DAILY_CHALLENGE, callback: onDailyChallenge });
      setShowPuzzleLossConfirm(true);
      return;
    }
    
    onDailyChallenge?.();
  }, [isGameInProgress, onDailyChallenge]);
  
  // Handler for Challenge Friend button
  const handleChallengeFriendClick = useCallback(() => {
    // Show confirmation if game is in progress
    if (isGameInProgress) {
      setPendingAction({ type: PUZZLE_LOSS_ACTIONS.CHALLENGE, callback: onChallengeFriend });
      setShowPuzzleLossConfirm(true);
      return;
    }
    
    onChallengeFriend?.();
  }, [isGameInProgress, onChallengeFriend]);
  
  // Confirm puzzle loss action
  const confirmPuzzleLossAction = useCallback(() => {
    setShowPuzzleLossConfirm(false);
    pendingAction?.callback?.();
    setPendingAction(null);
  }, [pendingAction]);
  
  // Cancel puzzle loss action
  const cancelPuzzleLossAction = useCallback(() => {
    setShowPuzzleLossConfirm(false);
    setPendingAction(null);
  }, []);

  // Shared props for both headers
  const headerProps = {
    time,
    moves,
    difficulty,
    onDifficultySelect: handleDifficultySelect,
    isPaused,
    isRunning,
    onPause,
    onPrevious: handlePreviousClick,
    onNewImage: handleNewImageClick,
    canGoBack,
    hasNewImages,
    onReset,
    onSettings,
    onLeaderboard,
    onDailyChallenge: handleDailyChallengeClick,
    onChallengeFriend: handleChallengeFriendClick,
    onHistory,
    onHint,
    onHelp,
    hasPlayedDailyToday,
    notificationCount,
    notifications,
    onMarkNotificationsRead,
    onViewChallengeNotification,
    hasUnplacedPieces,
    isComplete,
    imageUrl
  };

  return (
    <>
      {/* Floating Reference Image - Mobile only */}
      {isMobile && (
        <ReferenceImage
          imageUrl={imageUrl}
          isLandscape={isLandscape}
          isDragHovering={isDragHovering}
          isOverlayActive={pieceOverlay?.isOverlayActive}
          isImageOpen={isImageOpen}
          setIsImageOpen={setIsImageOpen}
          refImageRef={refImageRef}
          hideReferenceImage={hideReferenceImage}
        />
      )}

      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/50 shadow-sm safe-area-top" data-testid="game-header">
        {/* No New Images Message Banner */}
        {showNoNewImagesMessage && (
          <div className="bg-amber-100 dark:bg-amber-900/50 border-b border-amber-300 dark:border-amber-700 px-4 py-2 text-center">
            <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
              End of New Images - Please use Previous to continue with a previously viewed Puzzle
            </p>
          </div>
        )}
        <div className={cn(
          "container mx-auto px-2 sm:px-4 py-2",
          isMobile && "pr-[82px] sm:pr-[106px]",
          isLandscape && "py-1 pr-[60px]",
          !isMobile && "py-3 lg:py-4 max-w-6xl"
        )}>
          {/* Desktop Layout */}
          {!isMobile && <DesktopHeader {...headerProps} />}
          
          {/* Mobile Layout */}
          {isMobile && <MobileHeader {...headerProps} isLandscape={isLandscape} onHelp={onHelp} />}
        </div>
      </div>
      
      {/* Difficulty Change Confirmation Dialog */}
      <DifficultyConfirmDialog
        showDifficultyConfirm={showDifficultyConfirm}
        setShowDifficultyConfirm={setShowDifficultyConfirm}
        pendingDifficulty={pendingDifficulty}
        onConfirm={confirmDifficultyChange}
        onCancel={cancelDifficultyChange}
      />
      
      {/* Puzzle Loss Confirmation Dialog */}
      <NewPuzzleConfirmDialog
        open={showPuzzleLossConfirm}
        onOpenChange={setShowPuzzleLossConfirm}
        onConfirm={confirmPuzzleLossAction}
        onCancel={cancelPuzzleLossAction}
        actionType={pendingAction?.type}
      />
    </>
  );
}
